import axios from 'axios';

// Create an instance of axios with default config
const api = axios.create({
  baseURL: 'https://unitrade-backend-946167918479.europe-west1.run.app/api',
  withCredentials: true,  // Important for sending cookies with cross-origin requests
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
  withXSRFToken: true,
});
if (typeof window !== 'undefined') {
  // eslint-disable-next-line no-console
  console.log('UniTrade API base URL:', api.defaults.baseURL);
}

// Flag to prevent multiple refresh token requests
let isRefreshing = false;
let failedQueue = [];

// Process the queue of failed requests
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Add a request interceptor to include the auth token in all requests
api.interceptors.request.use(
  (config) => {
    // Skip adding token for token refresh requests to avoid infinite loops
    if (config.skipAuthInterceptor) {
      return config;
    }
    
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Don't set Content-Type for FormData, let the browser handle it
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // If the error is not 401, the request was already retried, or it's a login request, reject
    if (!error.response || error.response.status !== 401 || originalRequest._retry || 
        originalRequest.url.endsWith('/login/')) {
      return Promise.reject(error);
    }
    
    // If the request is for token refresh, don't retry
    if (originalRequest.url === '/users/token/refresh/') {
      return Promise.reject(error);
    }
    
    originalRequest._retry = true;
    
    if (!isRefreshing) {
      isRefreshing = true;
      
      try {
        // Import refreshToken function dynamically to avoid circular dependency
        const { refreshToken } = await import('./authService');
        const newToken = await refreshToken();
        
        // Update the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        
        // Process any queued requests
        processQueue(null, newToken);
        
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    } else {
      // If a refresh is already in progress, queue this request
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(newToken => {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      }).catch(err => {
        return Promise.reject(err);
      });
    }
  }
);

export default api;
